import range from 'lodash/range'
import * as React from 'react'

import { Target, TargetType } from 'main/components/school/Callout'
import { SchoolListingType } from 'main/gql/globalTypes'
import { AssetName } from 'main/util/asset'
import { NetworkRating } from 'main/util/networks'

const WhitelabelContext = React.createContext<IWhitelabelContext | undefined>(undefined)

interface IWhitelabelOptions {
  [key: string]: string | boolean
}

interface IWhitelabelContext {
  enabledProducts: string[]
  isWhitelabeled: boolean
  i18nSuffix: string
  getAsset: (key: string) => string | undefined
  networkAbbr: string | undefined
  networkId: string | undefined
  networkName: string | undefined
  networkAutoMigrateCcms: boolean | null | undefined
  subsidyAltLookup: () => string
  ageRangeLookup: () => number[]
  showByDefaultExceptIfFalse: (targetLookup: string) => boolean
  hideByDefaultExceptIfTrue: (targetLookup: string) => boolean
  evaluateToFalseExceptIfTrue: (targetLookup: string) => boolean
  evaluateToTrueExceptIfFalse: (targetLookup: string) => boolean
  listingNameColor: (target: Target) => string | undefined
  networkRatingData: () => INetworkRatingData
  networkSubsidies: any[]
  networkProgramOfferings: any[]
  networkOrigin: string | null | undefined
  availableLocales: string[]
}

interface IProps {
  enabledProducts: string[]
  networkId: string | undefined
  networkName: string | undefined
  networkAutoMigrateCcms: boolean | null | undefined
  whitelabelOptions: IWhitelabelOptions
  networkRatings: NetworkRating[]
  children: any
  subsidiesForNetwork: any[]
  networkProgramOfferings: any[]
  networkOrigin: string | null | undefined
  availableLocales: string[]
}

interface INetworkFilterFormOption {
  label: string
  slug: string
  symbolCount: number | null
}

interface INetworkRatingData {
  filterOptions: INetworkFilterFormOption[]
  color: string | undefined
  symbol: string | undefined
  ratingSystemTitle: string | undefined
  ratingSystemSubtitle: string | undefined
  ratingLearnMore: string | undefined
  allowableFilterOptionValues: string[] | undefined
  stars: (count: number | null) => React.ReactElement | string
}

const Networks = {
  ELI: 'ELI',
  NM: 'NM',
  FLDOE: 'FLDOE',
}

function _getAsset(
  whitelabelOptions: IWhitelabelOptions,
  key: string,
): string | boolean | undefined {
  return whitelabelOptions[key]
}

export function getAsset(
  whitelabelOptions: IWhitelabelOptions,
  targetLookup: string,
): string | undefined {
  const optionValue = _getAsset(whitelabelOptions, targetLookup)
  return optionValue === undefined ? undefined : optionValue.toString()
}

function _getAssetAsBoolean(
  whitelabelOptions: IWhitelabelOptions,
  targetLookup: string,
  defaultValue: boolean,
): boolean {
  const optionValue = _getAsset(whitelabelOptions, targetLookup)
  return optionValue === undefined ? defaultValue : !!optionValue
}

function WhitelabelProvider(props: IProps) {
  const {
    enabledProducts,
    whitelabelOptions,
    children,
    networkId,
    networkName,
    networkAutoMigrateCcms,
    networkRatings,
    subsidiesForNetwork,
    networkProgramOfferings,
    networkOrigin,
    availableLocales,
  } = props

  const isWhitelabeled = !!Object.keys(whitelabelOptions).length
  const networkAbbr = getAsset(whitelabelOptions, AssetName.NETWORK_ABBR)
  const i18nSuffix = getAsset(whitelabelOptions, AssetName.I18N_SUFFIX) || ''

  const networkSubsidies =
    subsidiesForNetwork &&
    subsidiesForNetwork.map(sub => {
      // remove __typename sent from graphql
      const { __typename, ...rest } = sub
      return rest
    })

  function showByDefaultExceptIfFalse(targetLookup: string): boolean {
    return _getAssetAsBoolean(whitelabelOptions, targetLookup, true)
  }

  function hideByDefaultExceptIfTrue(targetLookup: string): boolean {
    return _getAssetAsBoolean(whitelabelOptions, targetLookup, false)
  }

  function evaluateToFalseExceptIfTrue(targetLookup: string): boolean {
    return hideByDefaultExceptIfTrue(targetLookup)
  }

  function evaluateToTrueExceptIfFalse(targetLookup: string): boolean {
    return showByDefaultExceptIfFalse(targetLookup)
  }

  function subsidyAltLookup(): string {
    switch (networkAbbr) {
      case Networks.ELI:
        return 'NETWORK-ELI-SUBSIDY-NAME'
      case Networks.NM:
        return 'NETWORK-NM-SUBSIDY-NAME'
      default:
        return 'NETWORK-DEFAULT-SUBSIDY-NAME'
    }
  }

  // 0 to 24 months: each month
  // 2 to 5 years: each half-year
  // 5 to 17 years 1: each year
  function ageRangeLookup(): number[] {
    const SECOND_AGE_RANGE = range(12 * 2, 12 * 5, 6)
    const THIRD_AGE_RANGE = range(12 * 5, 12 * 18, 12)

    switch (networkAbbr) {
      case Networks.ELI:
        return range(1, 12 * 2)
          .concat(SECOND_AGE_RANGE)
          .concat(THIRD_AGE_RANGE)
      default:
        return range(0, 12 * 2)
          .concat(SECOND_AGE_RANGE)
          .concat(THIRD_AGE_RANGE)
    }
  }

  function listingNameColor(target: Target): string | undefined {
    const primary: string | undefined = getAsset(whitelabelOptions, AssetName.PRIMARY_COLOR)
    const secondary: string | undefined = getAsset(whitelabelOptions, AssetName.SECONDARY_COLOR)

    switch (target._type) {
      case TargetType.School:
        return primary
      case TargetType.ExternalSchool:
        return secondary
      case TargetType.SchoolListing:
        return target.listing.type === SchoolListingType.INTERNAL ? primary : secondary
    }
  }

  // conglomeration of networks.whitelabel_options and network_ratings table
  // so that everything needed for the custom network filters (  like ELI paths to quality) can be
  // accessed all via this instead of poluting the whitelabelcontext api.
  function networkRatingData(): INetworkRatingData {
    const filterOptions = networkRatings
      .sort((a, b) => a.position - b.position)
      .map(nr => ({
        label: nr.label,
        slug: nr.slug,
        symbolCount: nr.symbolCount,
      }))

    const color = getAsset(whitelabelOptions, AssetName.NETWORK_RATING_COLOR)
    const symbol = getAsset(whitelabelOptions, AssetName.NETWORK_RATING_SYMBOL)

    function stars(count: number | null): React.ReactElement | string {
      if (symbol && count && count > 0) {
        return <span style={{ color }}>{symbol.repeat(count)}</span>
      }
      return ''
    }

    return {
      color,
      symbol,
      ratingSystemTitle: getAsset(whitelabelOptions, AssetName.NETWORK_RATING_SYSTEM_TITLE),
      ratingSystemSubtitle: getAsset(whitelabelOptions, AssetName.NETWORK_RATING_SYSTEM_SUBTITLE),
      ratingLearnMore: getAsset(whitelabelOptions, AssetName.NETWORK_RATING_LEARN_MORE),
      allowableFilterOptionValues: filterOptions.map(nr => nr.slug),
      filterOptions,
      stars,
    }
  }

  return (
    <WhitelabelContext.Provider
      value={{
        enabledProducts,
        isWhitelabeled,
        i18nSuffix,
        getAsset: a => getAsset(whitelabelOptions, a),
        networkAbbr,
        networkId,
        networkName,
        networkAutoMigrateCcms,
        showByDefaultExceptIfFalse,
        hideByDefaultExceptIfTrue,
        evaluateToFalseExceptIfTrue,
        evaluateToTrueExceptIfFalse,
        subsidyAltLookup,
        ageRangeLookup,
        listingNameColor,
        networkRatingData,
        networkSubsidies,
        networkProgramOfferings,
        networkOrigin,
        availableLocales,
      }}
    >
      {children}
    </WhitelabelContext.Provider>
  )
}

function useWhitelabel() {
  const context = React.useContext(WhitelabelContext)
  if (context === undefined) {
    throw new Error('WhitelabelContext must be used within a WhitelabelProvider')
  }
  return context
}

export {
  WhitelabelContext,
  useWhitelabel,
  WhitelabelProvider,
  IWhitelabelContext,
  Networks,
  INetworkFilterFormOption,
}
